import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEntitledChallengeIds } from '../membership/useEntitledChallengeIds'
import { useTeamMemberships } from '../membership/useTeamMemberships'
import {
  getStoredActiveChallengeId,
  setActiveChallengeId,
  subscribeActiveChallengeId,
} from './activeChallenge'
import { useChallengeVisibility } from './useChallenges'

/**
 * Current challenge for reads: the route `:challengeId` when this user can
 * open it, otherwise the stored workspace challenge, otherwise the first
 * visible membership. A stored id this user cannot open falls through and is
 * left in place. A route they cannot open resolves to '' so the route guard
 * can send them to a workspace or the join screen.
 *
 * Storage is committed on explicit switcher picks, selecting a challenge to
 * join, and successful join/create/pay — not on every route visit, and not
 * when a hidden id is ignored.
 */
export function useChallengeResolution() {
  const { challengeId: routeId } = useParams()
  const [storedId, setStoredId] = useState(getStoredActiveChallengeId)
  const visibility = useChallengeVisibility()
  const membershipsQuery = useTeamMemberships()
  const entitledQuery = useEntitledChallengeIds()

  useEffect(() => subscribeActiveChallengeId(() => setStoredId(getStoredActiveChallengeId())), [])

  const memberships = membershipsQuery.data
  const entitledIds = entitledQuery.data
  const catalogSettled = visibility.ready
  const membershipsSettled = !membershipsQuery.isLoading
  const entitledSettled = !entitledQuery.isLoading

  const routeHidden = catalogSettled && !!routeId && !visibility.canSee(routeId)
  const storedHidden = catalogSettled && !!storedId && !visibility.canSee(storedId)
  const visibleMembershipId = membershipsSettled
    ? (memberships?.find((membership) => visibility.canSee(membership.challengeID))?.challengeID ??
      '')
    : ''
  const needsEntitlements = (routeHidden || storedHidden) && !visibleMembershipId
  const isResolving =
    !catalogSettled || !membershipsSettled || (needsEntitlements && !entitledSettled)

  const visibleEntitlementId =
    !isResolving && entitledSettled
      ? (entitledIds?.find((id) => visibility.canSee(id)) ?? '')
      : ''
  const storedUsable = !isResolving && storedId && visibility.canSee(storedId) ? storedId : ''
  const fallbackId =
    visibleMembershipId || (storedHidden ? visibleEntitlementId : '')
  const id = isResolving || routeHidden ? '' : routeId || storedUsable || fallbackId

  useEffect(() => {
    if (isResolving || storedId || routeId || !visibleMembershipId) return
    setActiveChallengeId(visibleMembershipId)
  }, [isResolving, storedId, routeId, visibleMembershipId])

  return {
    id,
    isResolving,
    canSee: visibility.canSee,
    hasVisibleWorkspace: !isResolving && (!!visibleMembershipId || !!visibleEntitlementId),
  }
}

export function useChallengeId(): string {
  return useChallengeResolution().id
}

export function useSetActiveChallengeId() {
  return useCallback((challengeId: string) => {
    setActiveChallengeId(challengeId)
  }, [])
}
